import { recoverToLatestBuild } from '@/lib/helpers/appUpdateRecovery.ts'
import {
  decideChunkRecoveryAction,
  pruneReloadAttempts,
} from '@/lib/helpers/chunkRecoveryDecisions.ts'
import { probeConnectivity } from '@/lib/connectivityProbe.ts'
import { renderChunkFailureFallback } from '@/lib/helpers/renderChunkFailureFallback.ts'
import { isChunkLoadErrorMessage } from '@/lib/helpers/chunkLoadError.ts'

/**
 * Global handler for chunk loading errors (dynamic import failures).
 *
 * When a new version is deployed, old JS chunks are removed. A user holding a
 * stale index.html requests old chunk hashes, the dynamic import 404s, and this
 * handler reloads to fetch the new build.
 *
 * The reload is bounded. A chunk that keeps failing even after a clean reload
 * (a genuinely missing/unservable build artifact, not a stale index) would
 * otherwise reload forever on a blank page. We record each reload attempt and,
 * after MAX_RELOAD_ATTEMPTS within ATTEMPT_WINDOW_MS, stop and render a static
 * retry screen instead of looping. Attempts age out of the window on their own —
 * there is deliberately no success/clear timer, because a timer firing during
 * the (multi-second) recovery sequence would wipe the counter before the reload
 * landed and defeat the loop guard.
 */

const ATTEMPTS_STORAGE_KEY = 'chunk_error_reload_attempts'
const ATTEMPT_WINDOW_MS = 120_000 // 2 minutes
const MAX_RELOAD_ATTEMPTS = 4

// In-memory guard: a failing route's import graph rejects several modules at
// once, so many `unhandledrejection` events fire in the same tick. Only the
// first should drive recovery — otherwise the attempt counter is exhausted (and
// caches cleared) several times over in a single load. Set synchronously, before
// the first await, so the sibling rejections all see it.
let recovering = false

function readRecentAttempts(now: number): number[] {
  try {
    const raw = localStorage.getItem(ATTEMPTS_STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return pruneReloadAttempts(parsed as number[], now, ATTEMPT_WINDOW_MS)
  } catch {
    return []
  }
}

function recordAttempt(now: number, recent: number[]): void {
  try {
    localStorage.setItem(ATTEMPTS_STORAGE_KEY, JSON.stringify([...recent, now]))
  } catch {
    // localStorage can throw (private mode / quota). Recovery still proceeds;
    // we just lose the loop guard for this attempt.
  }
}

function clearAttempts(): void {
  try {
    localStorage.removeItem(ATTEMPTS_STORAGE_KEY)
  } catch {
    // ignore
  }
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return isChunkLoadErrorMessage(error.message)
}

async function handleChunkError(): Promise<void> {
  // Only the first chunk error per page load drives recovery.
  if (recovering) return
  recovering = true

  const now = Date.now()
  const recent = readRecentAttempts(now)

  // Gate recovery behind connectivity checks. Clearing Cache Storage while
  // offline would wipe the precached app shell (workbox-precache, app-shell,
  // offline.html) and guarantee a white screen on the forced reload — the exact
  // failure mode recovery is meant to prevent online.
  const isOnline = navigator.onLine
  const probeOnline = isOnline ? await probeConnectivity() : false

  const decision = decideChunkRecoveryAction({
    isOnline,
    probeOnline,
    recentAttempts: recent.length,
    maxAttempts: MAX_RELOAD_ATTEMPTS,
  })

  if (decision.action === 'skip') {
    // Allow a later event (e.g. once connectivity returns) to retry.
    recovering = false
    switch (decision.reason) {
      case 'offline':
        console.warn(
          '[ChunkErrorHandler] Offline — preserving caches so the offline shell stays usable',
        )
        return
      case 'unreachable':
        console.warn(
          '[ChunkErrorHandler] Server unreachable despite navigator.onLine — preserving caches',
        )
        return
    }
  }

  if (decision.action === 'giveup') {
    // Reloading can't fix a chunk that keeps failing while the server is
    // reachable. Stop the loop and show a dependency-free retry screen so the
    // user sees something actionable instead of an endlessly-reloading blank.
    console.error(
      `[ChunkErrorHandler] Chunk still failing after ${recent.length} reload(s) within ` +
        `${ATTEMPT_WINDOW_MS / 1000}s — stopping the reload loop and showing a retry screen`,
    )
    renderChunkFailureFallback({ onRetry: clearAttempts })
    return
  }

  // recover
  recordAttempt(now, recent)
  await recoverToLatestBuild({
    reloadParam: '_reload',
    logPrefix: 'ChunkErrorHandler',
  })
}

export function setupChunkErrorHandler(): void {
  // Handle unhandled promise rejections (dynamic imports)
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      console.error('[ChunkErrorHandler] Chunk load error detected:', event.reason)
      event.preventDefault()
      void handleChunkError()
    }
  })

  // Handle regular errors (fallback)
  window.addEventListener('error', (event: ErrorEvent) => {
    if (isChunkLoadError(event.error)) {
      console.error('[ChunkErrorHandler] Chunk load error detected:', event.error)
      event.preventDefault()
      void handleChunkError()
    }
  })

  // Listen for SW notification about chunk 404
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      if (event.data?.type === 'CHUNK_LOAD_ERROR') {
        console.error('[ChunkErrorHandler] SW reported chunk 404:', event.data.url)
        void handleChunkError()
      }
    })
  }

  console.log('[ChunkErrorHandler] Initialized')
}
